import axios from "axios";
import {
  API_BASE_URL,
  STRIPE_MICRO_SERVICE_API_BASE_URL,
  ENV,
} from "./appConfig";
import { errorHandler } from "../helpers/errorHandler";
import { getLocal } from "../helpers/localStore";
import { LOCAL_STORAGE } from "./constants";
import {
  isUnauthorized,
  performLogout,
  isLogoutInProgress,
} from "@/shared/helpers/authHelper";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";

const AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

export const StripeMicroServiceAxiosInstance = axios.create({
  baseURL: STRIPE_MICRO_SERVICE_API_BASE_URL,
});

/**
 * Holds axios cancel tokens
 */
let requestCall: Record<string, any> = {};
/**
 * @method takeLatestRequest
 * @description cancel previouse request of same url and type if not fullfilled
 * @param {*} config
 * @param {*} requestType
 */
export const takeLatestRequest = (
  config: Record<string, any> = {},
  requestType = "rahulppatidar"
) => {
  if (requestCall[requestType]) {
    requestCall[requestType].cancel(
      // { canceledReq: 'Only one request allowed at a time. Cancelling first' }
      { canceledReq: "Canceled due to multiple request" }
    );
  }
  requestCall[requestType] = axios.CancelToken.source();
  config.cancelToken = requestCall[requestType].token;
  return AxiosInstance(config);
};

/**
 * @method check is error because of cancel request
 * @param {*} error
 */
export const isCancelRequest = (error: unknown) => axios.isCancel(error);

const axiosInstanceReqInterceptor = function (
  config: InternalAxiosRequestConfig
) {
  const token = getLocal(LOCAL_STORAGE.TOKEN_NAME);
  const requestConfig = config as any;
  requestConfig.headers = requestConfig.headers || {};
  const headers = requestConfig.headers as Record<string, any>;
  // Public API client key - backend validates this
  if (ENV.PUBLIC_API_CLIENT_KEY) {
    headers.apiAccessPrivateKey = ENV.PUBLIC_API_CLIENT_KEY;
  }
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }
  headers["x-caller-url"] =
    `${window.location.origin}${window.location.pathname}`;
  return config;
};

export const handleAxiosResponseError = (error: AxiosError) => {
  if (isUnauthorized(error)) {
    performLogout();
    return Promise.reject(error);
  }
  if (!isLogoutInProgress()) {
    errorHandler(error);
  }
  return Promise.reject(error);
};

// A request interceptor
AxiosInstance.interceptors.request.use(
  axiosInstanceReqInterceptor,
  function (error: AxiosError) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// A response interceptor
AxiosInstance.interceptors.response.use(function (response) {
  // Do something with response data
  return response;
}, handleAxiosResponseError);

const stripeMicroServiceAxiosInstanceReqInterceptor = function (
  config: InternalAxiosRequestConfig
) {
  const token = getLocal(LOCAL_STORAGE.TOKEN_NAME);
  const requestConfig = config as any;
  requestConfig.headers = requestConfig.headers || {};
  const headers = requestConfig.headers as Record<string, any>;
  // Public Stripe app client identifiers - backend validates these
  if (ENV.PUBLIC_STRIPE_APP_TOKEN) {
    headers.app_token = ENV.PUBLIC_STRIPE_APP_TOKEN;
  }
  if (ENV.PUBLIC_STRIPE_APP_CLIENT_KEY) {
    headers.app_secret = ENV.PUBLIC_STRIPE_APP_CLIENT_KEY;
  }
  if (ENV.PUBLIC_API_CLIENT_KEY) {
    headers.apiAccessPrivateKey = ENV.PUBLIC_API_CLIENT_KEY;
  }
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }
  headers["x-caller-url"] =
    `${window.location.origin}${window.location.pathname}`;
  return config;
};

// A request interceptor
StripeMicroServiceAxiosInstance.interceptors.request.use(
  stripeMicroServiceAxiosInstanceReqInterceptor,
  function (error: AxiosError) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// A response interceptor
StripeMicroServiceAxiosInstance.interceptors.response.use(function (response) {
  // Do something with response data
  return response;
}, handleAxiosResponseError);

export default AxiosInstance;
